import React, { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl, downloadXlsx } from "@/lib/api";
import { fmtEuro, MONTHS_EL_SHORT } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Upload, Search, Save, AlertTriangle, Table2, Tags, Sparkles, TrendingUp, ArrowUp, ArrowDown, Paperclip, X, Trash2, Plus, FileSpreadsheet } from "lucide-react";

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1));

function SupplierDocs({ row, canEdit, onClose, onChanged }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["supplier-files", row.id],
    queryFn: async () => (await api.get(`/finance/supplier-expenses/${row.id}/files`)).data,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["supplier-files", row.id] });
    onChanged?.();
  };
  const add = useMutation({
    mutationFn: async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      const up = (await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      return (await api.post(`/finance/supplier-expenses/${row.id}/files`,
        { file_id: up.file_id, filename: f.name })).data;
    },
    onSuccess: () => { refresh(); toast.success("Το παραστατικό επισυνάφθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/finance/supplier-expenses/files/${id}`)).data,
    onSuccess: () => { refresh(); setPreview(null); toast.success("Το παραστατικό αφαιρέθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="sx-docs-modal">
      <div className="absolute inset-0 bg-[#45423C]/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif text-xl text-[#45423C]">Παραστατικά · {row.supplier}</h3>
            <p className="text-xs text-[#6F6A60] mt-1">Χρήση {row.year} · σύνολο {fmtEuro(row.total)}</p>
          </div>
          <button data-testid="sx-docs-close" onClick={onClose} className="text-[#6F6A60] hover:text-[#45423C]"><X className="h-5 w-5" /></button>
        </div>

        {canEdit && (
          <div>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="sx-docs-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) add.mutate(f); e.target.value = ""; }} />
            <button data-testid="sx-docs-upload" onClick={() => fileRef.current?.click()} disabled={add.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
              <Upload className="h-4 w-4" /> {add.isPending ? "Ανέβασμα…" : "Ανέβασμα τιμολογίου (PDF/φωτό)"}
            </button>
          </div>
        )}

        {isLoading ? <p className="text-sm text-[#6F6A60]">Φόρτωση…</p> : (
          <div className="space-y-2">
            {(data?.files || []).length === 0 && <p className="text-sm text-[#8A8577]">Δεν έχει επισυναφθεί παραστατικό.</p>}
            {(data?.files || []).map((f) => (
              <div key={f.id} data-testid={`sx-doc-${f.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#E3DED4] px-3 py-2">
                <button onClick={() => setPreview(f)} className="flex items-center gap-2 text-sm text-[#45423C] hover:underline truncate">
                  <Paperclip className="h-3.5 w-3.5 text-[#C86235]" /> <span className="truncate">{f.filename || f.file_id}</span>
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`sx-doc-open-${f.id}`}
                    className="text-xs font-semibold text-[#C86235] hover:underline">Άνοιγμα</a>
                  {canEdit && (
                    <button data-testid={`sx-doc-remove-${f.id}`} onClick={() => remove.mutate(f.id)}
                      className="text-[#991B1B] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {preview && (
          <object data={fileUrl(preview.file_id)} className="w-full h-[55vh] rounded-xl border border-[#E3DED4]" data-testid="sx-doc-preview">
            <div className="p-6 text-center text-sm text-[#6F6A60]">Η προεπισκόπηση δεν είναι διαθέσιμη — χρησιμοποιήστε το «Άνοιγμα».</div>
          </object>
        )}
      </div>
    </div>
  );
}

export default function SupplierExpenses() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(2026);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapDraft, setMapDraft] = useState({});
  const [aiRows, setAiRows] = useState(null);
  const [topMonth, setTopMonth] = useState("");
  const [docs, setDocs] = useState(null);
  const [rowEdit, setRowEdit] = useState(null);
  const fileRef = useRef(null);
  const pendingFile = useRef(null);

  const [bu] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["supplier-expenses", year, cat, q, bu],
    queryFn: async () => (await api.get(`/finance/supplier-expenses?year=${year}&category=${cat}&business_unit=${encodeURIComponent(bu)}&q=${encodeURIComponent(q)}`)).data,
  });
  const { data: cats } = useQuery({
    queryKey: ["expense-categories", year],
    queryFn: async () => (await api.get(`/finance/expense-categories?year=${year}`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["supplier-expenses"] });
    qc.invalidateQueries({ queryKey: ["expense-categories"] });
    qc.invalidateQueries({ queryKey: ["pl-year"] });
    qc.invalidateQueries({ queryKey: ["pl-period"] });
    qc.invalidateQueries({ queryKey: ["cashflow"] });
  };

  const upload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/finance/supplier-expenses/preview", fd)).data;
    },
    onSuccess: (d) => { setPreview(d); toast.success(`Βρέθηκαν ${d.rows_total} προμηθευτές · ${fmtEuro(d.grand_total)}`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const commit = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", pendingFile.current);
      return (await api.post("/finance/supplier-expenses/commit?replace=true", fd)).data;
    },
    onSuccess: (d) => {
      setPreview(null);
      refresh();
      toast.success(`Καταχωρήθηκαν ${d.saved} προμηθευτές για το ${d.year}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const saveMap = useMutation({
    mutationFn: async () => {
      const items = (cats?.categories || []).map((c) => ({
        code: c.code, label: c.label,
        account_code: mapDraft[c.code] ?? c.account_code,
        cost_center: c.cost_center,
      })).filter((c) => c.account_code);
      return (await api.put("/finance/expense-categories", items)).data;
    },
    onSuccess: (d) => { setMapDraft({}); refresh(); toast.success(`Αποθηκεύτηκαν ${d.saved} αντιστοιχίσεις`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const saveRow = useMutation({
    mutationFn: async (row) => (await api.post("/finance/supplier-expenses/row", {
      year, supplier: row.supplier, category_code: row.category_code,
      category_label: (cats?.categories || []).find((c) => c.code === row.category_code)?.label || row.category_code,
      months: row.months,
    })).data,
    onSuccess: () => { setRowEdit(null); refresh(); toast.success("Ο προμηθευτής αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const deleteRow = useMutation({
    mutationFn: async (id) => (await api.delete(`/finance/supplier-expenses/row/${id}`)).data,
    onSuccess: () => { setRowEdit(null); refresh(); toast.success("Η γραμμή διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const { data: top } = useQuery({
    queryKey: ["supplier-top", year, topMonth],
    queryFn: async () => (await api.get(`/finance/supplier-expenses/top?year=${year}&limit=10${topMonth ? `&month=${topMonth}` : ""}`)).data,
  });

  const aiSuggest = useMutation({
    mutationFn: async () => (await api.post(`/ai/categorize-suppliers?year=${year}`)).data,
    onSuccess: (d) => {
      setAiRows(d.suggestions);
      toast.success(d.count ? `Το AI πρότεινε κατηγορία για ${d.count} προμηθευτές` : "Δεν υπάρχουν προμηθευτές χωρίς κατηγορία");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const applyAi = useMutation({
    mutationFn: async (rows) => (await api.put("/finance/supplier-expenses/categorize",
      rows.map((r) => ({ supplier: r.supplier, account_code: r.account_code, cost_center: r.cost_center, year })))).data,
    onSuccess: (d) => { setAiRows(null); refresh(); toast.success(`Εφαρμόστηκαν σε ${d.saved} προμηθευτές`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    pendingFile.current = f;
    upload.mutate(f);
    e.target.value = "";
  };

  const totals = data?.monthly_totals || {};
  const unmapped = cats?.unmapped || 0;
  const catList = useMemo(() => cats?.categories || [], [cats]);

  return (
    <div className="space-y-6" data-testid="supplier-expenses-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Έξοδα ανά μήνα</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Έξοδα Προμηθευτών</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Ανεβάζετε το μηνιαίο αρχείο προμηθευτών (Print1.xlsx) — γραμμή ανά προμηθευτή, στήλη ανά μήνα.
            Κάθε ανέβασμα αντικαθιστά τα δεδομένα του έτους. Οι χρεώσεις θεωρούνται πληρωτέες στο τέλος κάθε μήνα.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <select data-testid="sx-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
            {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {canEdit && (
            <>
              <button data-testid="sx-ai-suggest" onClick={() => aiSuggest.mutate()} disabled={aiSuggest.isPending}
                className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63] disabled:opacity-50">
                <Sparkles className="h-4 w-4" /> {aiSuggest.isPending ? "Το AI σκέφτεται…" : "AI: κατηγορίες"}
              </button>
              <input ref={fileRef} data-testid="sx-file" type="file" accept=".xlsx" className="hidden" onChange={onPick} />
              <button data-testid="sx-upload" onClick={() => fileRef.current?.click()} disabled={upload.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                <Upload className="h-4 w-4" /> {upload.isPending ? "Ανάγνωση…" : "Ανέβασμα αρχείου"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Σύνολο έτους", fmtEuro(data?.grand_total)], ["Προμηθευτές", data?.count ?? 0],
          ["Κατηγορίες", catList.length], ["Προς αντιστοίχιση", unmapped]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`sx-kpi-${l}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
            <div className={`font-serif text-2xl mt-2 tabular ${l === "Προς αντιστοίχιση" && unmapped ? "text-[#991B1B]" : "text-[#45423C]"}`}>{v}</div>
          </div>
        ))}
      </div>

      {preview && (
        <div className="rounded-2xl border border-[#C86235] bg-[#FFFBEB] overflow-hidden" data-testid="sx-preview">
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#C86235]/30">
            <div>
              <h2 className="font-medium text-[#45423C]">Προεπισκόπηση · χρήση {preview.year}</h2>
              <p className="text-xs text-[#6F6A60] mt-0.5">
                {preview.rows_total} προμηθευτές · σύνολο {fmtEuro(preview.grand_total)} · αγνοήθηκαν {preview.skipped} κενές γραμμές
                {preview.unmapped_categories?.length ? ` · ${preview.unmapped_categories.length} κατηγορίες χωρίς αντιστοίχιση` : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <button data-testid="sx-commit" onClick={() => commit.mutate()} disabled={commit.isPending}
                className="rounded-xl bg-[#C86235] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {commit.isPending ? "Καταχώρηση…" : `Αντικατάσταση δεδομένων ${preview.year}`}
              </button>
              <button data-testid="sx-cancel" onClick={() => setPreview(null)} className="text-sm text-[#6F6A60] px-2 hover:text-[#45423C]">Άκυρο</button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-64">
            <table className="w-full text-xs whitespace-nowrap">
              <thead>
                <tr className="bg-white/60 text-left uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-3 py-2">Προμηθευτής</th>
                  <th className="px-3 py-2">Κατηγορία</th>
                  <th className="px-3 py-2 text-right">Σύνολο</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 40).map((r) => (
                  <tr key={r.supplier} className="border-t border-[#E3DED4]">
                    <td className="px-3 py-1.5 font-medium text-[#45423C]">{r.supplier}</td>
                    <td className="px-3 py-1.5 text-[#6F6A60]">{r.category_code} · {r.category_label}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtEuro(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {aiRows && (
        <div className="rounded-2xl border border-[#6E7A63] bg-[#F7F9F5] overflow-hidden" data-testid="sx-ai-panel">
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#6E7A63]/30">
            <div>
              <h2 className="font-medium text-[#45423C] flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#6E7A63]" /> Προτάσεις AI για προμηθευτές χωρίς κατηγορία
              </h2>
              <p className="text-xs text-[#6F6A60] mt-0.5">{aiRows.length} προτάσεις — ελέγξτε και εφαρμόστε. Μπορείτε να αλλάξετε λογαριασμό πριν την εφαρμογή.</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="sx-ai-apply" onClick={() => applyAi.mutate(aiRows)} disabled={applyAi.isPending || !aiRows.length}
                className="rounded-xl bg-[#6E7A63] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {applyAi.isPending ? "Εφαρμογή…" : "Εφαρμογή όλων"}
              </button>
              <button data-testid="sx-ai-cancel" onClick={() => setAiRows(null)} className="text-sm text-[#6F6A60] px-2 hover:text-[#45423C]">Άκυρο</button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-xs whitespace-nowrap">
              <thead>
                <tr className="bg-white/70 text-left uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-3 py-2">Προμηθευτής</th>
                  <th className="px-3 py-2 text-right">Σύνολο έτους</th>
                  <th className="px-3 py-2">Πρόταση λογαριασμού</th>
                  <th className="px-3 py-2">CC</th>
                  <th className="px-3 py-2">Αιτιολόγηση</th>
                </tr>
              </thead>
              <tbody>
                {aiRows.map((r, i) => (
                  <tr key={r.supplier} className="border-t border-[#E3DED4]" data-testid={`sx-ai-row-${i}`}>
                    <td className="px-3 py-1.5 font-medium text-[#45423C] max-w-[260px] truncate" title={r.supplier}>{r.supplier}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtEuro(r.total)}</td>
                    <td className="px-3 py-1.5">
                      <select data-testid={`sx-ai-acc-${i}`} value={r.account_code}
                        onChange={(e) => setAiRows((p) => p.map((x, j) => (j === i ? { ...x, account_code: e.target.value } : x)))}
                        className="rounded-lg border border-[#E3DED4] px-2 py-1 text-xs outline-none focus:border-[#45423C]">
                        {(cats?.accounts || []).map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5 text-[#6F6A60]">{r.cost_center}</td>
                    <td className="px-3 py-1.5 text-[#6F6A60] max-w-[280px] truncate" title={r.reason}>{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="sx-top">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-medium text-[#45423C] flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#C86235]" /> Top 10 προμηθευτές · {top?.month_label || "—"} {year}
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#6F6A60]">
              Σύνολο μήνα {fmtEuro(top?.month_total)} · top 10 {fmtEuro(top?.top_total)}
            </span>
            <select data-testid="sx-top-month" value={topMonth || top?.month || ""} onChange={(e) => setTopMonth(e.target.value)}
              className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
              {(top?.months_available || []).map((m) => <option key={m} value={m}>{MONTHS_EL_SHORT[m]}</option>)}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-2.5">#</th>
                <th className="px-4 py-2.5">Προμηθευτής</th>
                <th className="px-4 py-2.5">Κατηγορία</th>
                <th className="px-4 py-2.5 text-right">Μήνας</th>
                <th className="px-4 py-2.5 text-right">Προηγούμενος</th>
                <th className="px-4 py-2.5 text-right">Μεταβολή</th>
                <th className="px-4 py-2.5 text-right">% μήνα</th>
                <th className="px-4 py-2.5 text-right">Από αρχή έτους</th>
              </tr>
            </thead>
            <tbody>
              {(top?.rows || []).map((r, i) => (
                <tr key={r.supplier} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]" data-testid={`sx-top-row-${i}`}>
                  <td className="px-4 py-2 text-[#8A8577] tabular">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-[#45423C] max-w-[280px] truncate" title={r.supplier}>{r.supplier}</td>
                  <td className="px-4 py-2 text-xs text-[#6F6A60]">{r.category_label}</td>
                  <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(r.amount)}</td>
                  <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(r.prev)}</td>
                  <td className={`px-4 py-2 text-right tabular font-semibold ${r.delta > 0 ? "text-[#991B1B]" : r.delta < 0 ? "text-[#6E7A63]" : "text-[#6F6A60]"}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {r.delta > 0 ? <ArrowUp className="h-3 w-3" /> : r.delta < 0 ? <ArrowDown className="h-3 w-3" /> : null}
                      {fmtEuro(Math.abs(r.delta))}
                      {r.delta_pct !== null && <span className="text-[10px]">({r.delta_pct > 0 ? "+" : ""}{r.delta_pct}%)</span>}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{r.share_pct}%</td>
                  <td className="px-4 py-2 text-right tabular">{fmtEuro(r.ytd)}</td>
                </tr>
              ))}
              {(top?.rows || []).length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-[#6F6A60]">Δεν υπάρχουν χρεώσεις για αυτόν τον μήνα.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {canEdit && catList.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="sx-mapping">
          <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-medium text-[#45423C] flex items-center gap-2">
              <Tags className="h-4 w-4 text-[#C86235]" /> Αντιστοίχιση κατηγοριών σε λογαριασμούς USALI
              {unmapped > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#FEE2E2] px-2 py-0.5 text-[11px] font-semibold text-[#991B1B]">
                  <AlertTriangle className="h-3 w-3" /> {unmapped} χωρίς αντιστοίχιση
                </span>
              )}
            </h2>
            <button data-testid="sx-save-map" onClick={() => saveMap.mutate()} disabled={saveMap.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
              <Save className="h-4 w-4" /> Αποθήκευση αντιστοιχίσεων
            </button>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-2.5">Κωδικός</th>
                  <th className="px-4 py-2.5">Κατηγορία</th>
                  <th className="px-4 py-2.5 text-right">Προμηθευτές</th>
                  <th className="px-4 py-2.5 text-right">Σύνολο</th>
                  <th className="px-4 py-2.5">Λογαριασμός USALI</th>
                </tr>
              </thead>
              <tbody>
                {catList.map((c) => (
                  <tr key={c.code} className="border-t border-[#E3DED4]" data-testid={`sx-cat-${c.code}`}>
                    <td className="px-4 py-2 font-mono text-xs text-[#8A8577]">{c.code}</td>
                    <td className="px-4 py-2 text-[#45423C]">
                      {c.label}
                      {c.suggested && <span className="ml-2 rounded bg-[#F1EDE6] px-1.5 py-0.5 text-[10px] text-[#6F6A60]">πρόταση</span>}
                    </td>
                    <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{c.suppliers}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(c.total)}</td>
                    <td className="px-4 py-2">
                      <select data-testid={`sx-map-${c.code}`} value={mapDraft[c.code] ?? c.account_code ?? ""}
                        onChange={(e) => setMapDraft((p) => ({ ...p, [c.code]: e.target.value }))}
                        className={`rounded-lg border px-2 py-1.5 text-xs outline-none focus:border-[#45423C] ${
                          (mapDraft[c.code] ?? c.account_code) ? "border-[#E3DED4]" : "border-[#991B1B]"}`}>
                        <option value="">— επιλέξτε —</option>
                        {(cats?.accounts || []).map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-3">
          <h2 className="font-medium text-[#45423C] flex items-center gap-2">
            <Table2 className="h-4 w-4 text-[#C86235]" /> Χρεώσεις {year} ανά προμηθευτή
          </h2>
          <div className="relative ml-auto">
            <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="sx-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση προμηθευτή"
              className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C]" />
          </div>
          <select data-testid="sx-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)}
            className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
            <option value="all">Όλες οι κατηγορίες</option>
            {(data?.by_category || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.label}</option>)}
          </select>
          {canEdit && (
            <>
              <button data-testid="sx-template" onClick={() => downloadXlsx(`/finance/supplier-expenses/template.xlsx?year=${year}`, `Protypo_Promitheuton_${year}.xlsx`)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-xs font-semibold hover:border-[#B9AE9A]">
                <FileSpreadsheet className="h-3.5 w-3.5" /> Πρότυπο Excel
              </button>
              <button data-testid="sx-new-row" onClick={() => setRowEdit({ supplier: "", category_code: cat !== "all" ? cat : "999", months: {} })}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-3 py-2 text-xs font-semibold hover:opacity-90">
                <Plus className="h-3.5 w-3.5" /> Νέος προμηθευτής
              </button>
            </>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-3 py-2.5">Προμηθευτής</th>
                <th className="px-3 py-2.5">Κατηγορία</th>
                <th className="px-2 py-2.5 text-center">Παρ/κά</th>
                {MONTHS.map((m) => <th key={m} className="px-2 py-2.5 text-right">{MONTHS_EL_SHORT[Number(m)]}</th>)}
                <th className="px-3 py-2.5 text-right bg-[#56524A]">Σύνολο</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={16} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {(data?.rows || []).map((r) => (
                <tr key={r.id} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]" data-testid={`sx-row-${r.supplier}`}>
                  <td className="px-3 py-2 font-medium text-[#45423C] max-w-[280px] truncate" title={r.supplier}>
                    {canEdit ? (
                      <button data-testid={`sx-edit-${r.supplier}`} onClick={() => setRowEdit({ ...r })}
                        className="text-left hover:underline">{r.supplier}</button>
                    ) : r.supplier}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6F6A60]">
                    {r.category_label}
                    {!r.account_code && <span className="ml-1.5 text-[#991B1B] font-semibold">(χωρίς λογ.)</span>}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button data-testid={`sx-files-${r.supplier}`} onClick={() => setDocs(r)} title="Παραστατικά"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#C86235] hover:underline">
                      <Paperclip className="h-3.5 w-3.5" />{r.files || 0}
                    </button>
                  </td>
                  {MONTHS.map((m) => (
                    <td key={m} className={`px-2 py-2 text-right tabular ${(r.months?.[m] || 0) ? "" : "text-[#D8D0C4]"}`}>
                      {(r.months?.[m] || 0) ? fmtEuro(r.months[m]) : "—"}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular font-semibold bg-[#F7F5F1]">{fmtEuro(r.total)}</td>
                </tr>
              ))}
              {!isLoading && (data?.rows || []).length === 0 && (
                <tr><td colSpan={16} className="px-4 py-8 text-center text-[#6F6A60]">
                  Δεν υπάρχουν δεδομένα για το {year} — ανεβάστε το μηνιαίο αρχείο προμηθευτών.
                </td></tr>
              )}
            </tbody>
            {(data?.rows || []).length > 0 && (
              <tfoot>
                <tr className="bg-[#45423C] text-white" data-testid="sx-totals">
                  <td className="px-3 py-2.5 font-semibold" colSpan={3}>Σύνολα</td>
                  {MONTHS.map((m) => <td key={m} className="px-2 py-2.5 text-right tabular">{fmtEuro(totals[m])}</td>)}
                  <td className="px-3 py-2.5 text-right tabular font-semibold bg-[#56524A]">{fmtEuro(data?.grand_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {rowEdit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="sx-row-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl mt-10 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">{rowEdit.id ? "Επεξεργασία προμηθευτή" : "Νέος προμηθευτής"} · {year}</h3>
              <button data-testid="sx-row-close" onClick={() => setRowEdit(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Επωνυμία</label>
                  <input data-testid="sx-row-supplier" value={rowEdit.supplier} disabled={!!rowEdit.id}
                    onChange={(e) => setRowEdit({ ...rowEdit, supplier: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Εμπορική κατηγορία</label>
                  <select data-testid="sx-row-category" value={rowEdit.category_code || "999"}
                    onChange={(e) => setRowEdit({ ...rowEdit, category_code: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]">
                    {(cats?.categories || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.label}</option>)}
                    <option value="999">999 · Χωρίς κατηγορία</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {MONTHS.map((m) => (
                  <div key={m}>
                    <label className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold">{MONTHS_EL_SHORT[Number(m)]}</label>
                    <input data-testid={`sx-row-m${m}`} type="number" value={(rowEdit.months || {})[m] ?? ""}
                      onChange={(e) => setRowEdit({ ...rowEdit, months: { ...(rowEdit.months || {}), [m]: e.target.value } })}
                      placeholder="0"
                      className="mt-1 w-full rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="text-sm text-[#6F6A60]">
                  Σύνολο: <span className="font-semibold text-[#45423C] tabular">
                    {fmtEuro(MONTHS.reduce((s, m) => s + (Number((rowEdit.months || {})[m]) || 0), 0))}
                  </span>
                </div>
                <div className="flex gap-2">
                  {rowEdit.id && (
                    <button data-testid="sx-row-delete"
                      onClick={() => { if (window.confirm(`Διαγραφή του «${rowEdit.supplier}» από το ${year};`)) deleteRow.mutate(rowEdit.id); }}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold text-[#991B1B] hover:bg-[#FEF2F2]">
                      <Trash2 className="h-4 w-4" /> Διαγραφή
                    </button>
                  )}
                  <button data-testid="sx-row-save" onClick={() => saveRow.mutate(rowEdit)} disabled={!rowEdit.supplier?.trim() || saveRow.isPending}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                    <Save className="h-4 w-4" /> {saveRow.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {docs && <SupplierDocs row={docs} canEdit={canEdit} onClose={() => setDocs(null)} onChanged={refresh} />}
    </div>
  );
}
